import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  MenuItem,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import {
  Business,
  Person,
  Visibility,
  VisibilityOff,
  LocationOn,
  CheckCircle,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/Context';
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function Signup() {
  const navigate = useNavigate();
  const { signup } = useUser();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    // Personal Information
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    
    // Dispensary Information
    dispensaryName: '',
    state: 'MA',
    licenseType: 'recreational',
    address: '',
    city: '',
    zipCode: '',
  });

  const [errors, setErrors] = useState({});

  const steps = ['Account Info', 'Dispensary Details', 'Review'];

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      // Validate personal information
      if (!formData.username.trim()) {
        newErrors.username = 'Username is required';
      } else if (formData.username.length < 3) {
        newErrors.username = 'Username must be at least 3 characters';
      }

      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email address';
      }

      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (step === 1) {
      // Validate dispensary information
      if (!formData.dispensaryName.trim()) {
        newErrors.dispensaryName = 'Dispensary name is required';
      }

      if (!formData.address.trim()) {
        newErrors.address = 'Address is required';
      }

      if (!formData.city.trim()) {
        newErrors.city = 'City is required';
      }

      if (!formData.zipCode.trim()) {
        newErrors.zipCode = 'ZIP code is required';
      } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
        newErrors.zipCode = 'Invalid ZIP code';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSignup = async () => {
    if (!validateStep(1)) {
      return;
    }

    setLoading(true);
    setError('');

    const result = await signup({
      username: formData.username,
      email: formData.email,
      password: formData.password,
      dispensaryName: formData.dispensaryName,
      state: formData.state,
      licenseType: formData.licenseType,
      address: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}`,
    });

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person /> Personal Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Box>

            <TextField
              label="Username"
              value={formData.username}
              onChange={handleChange('username')}
              error={!!errors.username}
              helperText={errors.username}
              fullWidth
              required
              placeholder="johndoe"
            />

            <TextField
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              required
              placeholder="you@example.com"
            />

            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange('password')}
              error={!!errors.password}
              helperText={errors.password || 'Must be at least 8 characters'}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business /> Dispensary Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Box>

            <TextField
              label="Dispensary Name"
              value={formData.dispensaryName}
              onChange={handleChange('dispensaryName')}
              error={!!errors.dispensaryName}
              helperText={errors.dispensaryName}
              fullWidth
              required
              placeholder="Green Leaf Dispensary"
            />

            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="State"
                value={formData.state}
                onChange={handleChange('state')}
                fullWidth
                required
              >
                {US_STATES.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="License Type"
                value={formData.licenseType}
                onChange={handleChange('licenseType')}
                fullWidth
                required
              >
                <MenuItem value="recreational">Recreational</MenuItem>
                <MenuItem value="medical">Medical</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </TextField>
            </Stack>

            <TextField
              label="Street Address"
              value={formData.address}
              onChange={handleChange('address')}
              error={!!errors.address}
              helperText={errors.address}
              fullWidth
              required
              placeholder="123 Main Street"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn />
                  </InputAdornment>
                ),
              }}
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="City"
                value={formData.city}
                onChange={handleChange('city')}
                error={!!errors.city}
                helperText={errors.city}
                fullWidth
                required
                placeholder="Boston"
              />

              <TextField
                label="ZIP Code"
                value={formData.zipCode}
                onChange={handleChange('zipCode')}
                error={!!errors.zipCode}
                helperText={errors.zipCode}
                fullWidth
                required
                placeholder="02101"
              />
            </Stack>
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle /> Review Your Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Box>

            <Box sx={{ background: '#f8f9fa', p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Account
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formData.username}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formData.email}
              </Typography>
            </Box>

            <Box sx={{ background: '#f8f9fa', p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Dispensary
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formData.dispensaryName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formData.address}, {formData.city}, {formData.state} {formData.zipCode}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textTransform: 'capitalize' }}>
                License: {formData.licenseType}
              </Typography>
            </Box>

            <Alert severity="info">
              By creating an account, you agree to our Terms of Service and Privacy Policy.
            </Alert>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        px: 2,
        py: 4,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%', maxWidth: 600 }}
      >
        <Card
          sx={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}
        >
          <CardContent sx={{ p: 5 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Business
                sx={{
                  fontSize: 60,
                  color: 'primary.main',
                  mb: 2,
                }}
              />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Register Your Dispensary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Join the compliance platform and stay compliant with ease
              </Typography>
            </Box>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Step Content */}
            <Box sx={{ mb: 4 }}>
              {renderStepContent(activeStep)}
            </Box>

            {/* Navigation Buttons */}
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
                sx={{ minWidth: 100 }}
              >
                Back
              </Button>

              <Box sx={{ flex: 1 }} />

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSignup}
                  disabled={loading}
                  sx={{
                    minWidth: 150,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Account'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{
                    minWidth: 100,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  Next
                </Button>
              )}
            </Stack>

            {/* Footer */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <a
                  href="/login"
                  style={{ color: '#667eea', fontWeight: 600, textDecoration: 'none' }}
                >
                  Sign In
                </a>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
}
